import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { BillingLineSide } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isString, uniq } from 'lodash-es';
import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import useInvalidateQueriesOnInvoiceUpdate from '~hooks/useInvalidateQueriesOnInvoiceUpdate';
import { Job, JobInvoicing } from '~hooks/useJob/models';
import ApproveJobsJobDetails from '~pages/Approvals/ApproveJobs/components/ApproveJobsDetailsTabs/JobDetails';
import LoadsQueryContext, {
  LoadsQueryContextProvider,
} from '~pages/Approvals/Files/components/JobsTable/ExpandedBlock/LoadsQueryContext';
import LoadsTab from '~pages/Approvals/Files/components/JobsTable/ExpandedBlock/LoadsTab';
import JobsQueryContext from '~pages/Approvals/Files/components/OrdersList/ExpandedBlock/JobsQueryContext';
import FilesQueryContext from '~pages/Approvals/Files/FilesQueryContext';
import { makeLoadsJobFilters } from '~pages/Approvals/Files/loadsJobFilters';
import { makeJobsBillingLinesQueryKey } from '~pages/Approvals/Files/utils';
import ApprovalCardElevationContext from '~pages/Approvals/shared/ApprovalCardElevationContext';
import { makeBillingLineGroupsWithJobsFilters } from '~queries/useBillingLineGroupsQuery';
import { jobInvoicesQueryKey } from '~queries/useJobInvoicesQuery';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import ApproveChargesPageContext from '../ApproveChargesPageContext';
import { ChargesGrainTab } from '../approveChargesTabs';
import ChargesBillingLinesQueryContext from '../ChargesBillingLinesQueryContext';
import { BILLING_LINE_SIDES, getLinesOfSide } from '../chargeSelection';
import { chargesRaisedCardSx } from '../chargesGrid';
import {
  getLineSettlementNumber,
  isSideBilledPerLoad,
  JobCharges,
} from '../groupBillingLines';
import ChargeRowsTable from './ChargeRowsTable';
import ChargesAddLoadButton from './ChargesAddLoadButton';
import ChargesAddOnActions from './ChargesAddOnActions';
import ChargesApproveAllLoadsButton from './ChargesApproveAllLoadsButton';
import ChargesJobFiles from './ChargesJobFiles';
import ChargesJobRow from './ChargesJobRow';
import ChargesLoadsTable from './ChargesLoadsTable';
import ChargesSectionHeader from './ChargesSectionHeader';
import ChargesSidePanels from './ChargesSidePanels';

const OPEN_JOB_RAIL_WIDTH = '3px';

const REVIEW_COLUMN_WIDTHS = 'minmax(0, 1fr) minmax(320px, 30%)';

interface ChargesJobSectionProps {
  job: JobInvoicing;
}

export default function ChargesJobSection({ job }: ChargesJobSectionProps) {
  const { chargesByJobId } = useContext(ChargesBillingLinesQueryContext);
  const { grainTab } = useContext(ApproveChargesPageContext);
  const charges = chargesByJobId.get(job.id);

  return grainTab === ChargesGrainTab.HOURLY_JOBS ? (
    <JobGrainJob charges={charges} job={job} />
  ) : (
    <LoadGrainJob charges={charges} job={job} />
  );
}

interface JobBodyProps {
  charges?: JobCharges;
  isOpen: boolean;
  job: JobInvoicing;
  onToggle: () => void;
}

function JobGrainJob({ charges, job }: Pick<JobBodyProps, 'charges' | 'job'>) {
  const [isOpen, setIsOpen] = useState(false);
  const toggleIsOpen = useCallback(() => setIsOpen((isOpenNow) => !isOpenNow), []);

  return (
    <ChargesJobFrame isOpen={isOpen}>
      <ChargesJobRow
        charges={charges}
        isOpen={isOpen}
        job={job}
        onToggle={toggleIsOpen}
      />

      {isOpen && <HourlyJobReview job={job} />}
    </ChargesJobFrame>
  );
}

function LoadGrainJob({ charges, job }: Pick<JobBodyProps, 'charges' | 'job'>) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { filters } = useContext(FilesQueryContext);
  const { expandAllRequest } = useContext(ApproveChargesPageContext);
  const loadsFilters = useMemo(() => makeLoadsJobFilters(filters), [filters]);
  const [isOpen, setIsOpen] = useState(expandAllRequest?.isExpanded ?? false);
  const toggleIsOpen = useCallback(() => setIsOpen((isOpenNow) => !isOpenNow), []);

  useEffect(() => {
    if (expandAllRequest) {
      setIsOpen(expandAllRequest.isExpanded);
    }
  }, [expandAllRequest]);

  return (
    <LoadsQueryContextProvider companyId={companyId} filters={loadsFilters} job={job}>
      <LoadGrainJobBody
        charges={charges}
        isOpen={isOpen}
        job={job}
        onToggle={toggleIsOpen}
      />
    </LoadsQueryContextProvider>
  );
}

const timeBasedSideNoteKeys = {
  [BillingLineSide.PAYABLE]: 'approvals.approve_charges.time_based_side.payable',
  [BillingLineSide.RECEIVABLE]: 'approvals.approve_charges.time_based_side.receivable',
} satisfies Record<BillingLineSide, string>;

const getTimeBasedSides = (charges?: JobCharges) =>
  charges
    ? BILLING_LINE_SIDES.filter(
        (side) => charges[side].hasLines && !isSideBilledPerLoad(charges, side),
      )
    : [];

function LoadGrainJobBody({ charges, isOpen, job, onToggle }: JobBodyProps) {
  const { loads, tickets } = useContext(LoadsQueryContext);
  const timeBasedSides = getTimeBasedSides(charges);
  const perLoadJobLines = (charges?.jobLines ?? []).filter(
    (row) => !timeBasedSides.includes(row.side),
  );
  const [hasBeenOpened, setHasBeenOpened] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setHasBeenOpened(true);
    }
  }, [isOpen]);

  return (
    <ChargesJobFrame isOpen={isOpen}>
      <ChargesJobRow
        charges={charges}
        isOpen={isOpen}
        job={job}
        loads={loads}
        onToggle={onToggle}
        primaryAction={<ChargesApproveAllLoadsButton />}
        tickets={tickets}
      />

      {/* Kept mounted while closed: the loads tab clears its data when it unmounts */}
      <Box
        sx={(theme) => ({
          bgcolor: theme.brandV2.colors.treadGray8,
          display: isOpen ? 'block' : 'none',
          pb: 1.5,
        })}
      >
        {isOpen && (
          <ChargesSectionHeader
            charges={charges}
            job={job}
            readOnlySides={timeBasedSides}
          />
        )}
        {hasBeenOpened && (
          <Box sx={nestedTableCardSx}>
            <LoadsTab renderLoadsTable={(props) => <ChargesLoadsTable {...props} />} />
            <ChargesAddLoadButton />
          </Box>
        )}
        {charges && timeBasedSides.length > 0 && (
          <TimeBasedSideNotes charges={charges} sides={timeBasedSides} />
        )}
        {perLoadJobLines.length > 0 && (
          <Box sx={[nestedTableCardSx, { mt: 1.75 }]}>
            <ChargeRowsTable
              nameHeading={t('approvals.approve_charges.columns.job_line')}
              rows={perLoadJobLines}
            />
          </Box>
        )}
        {timeBasedSides.length < BILLING_LINE_SIDES.length && (
          <>
            <Typography sx={nestedSectionTitleSx}>
              {t('approvals.approve_charges.add_ons_section_title')}
            </Typography>
            <Box sx={nestedTableCardSx}>
              <JobAddOns charges={charges} excludedSides={timeBasedSides} job={job} />
            </Box>
          </>
        )}
      </Box>
    </ChargesJobFrame>
  );
}

function HourlyJobReview({ job }: Pick<JobBodyProps, 'job'>) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { order, updateJobInQueryData } = useContext(JobsQueryContext);
  const { reload } = useContext(ChargesBillingLinesQueryContext);
  const filesCard = useMemo(() => <ChargesJobFiles job={job} />, [job]);
  const sidePanels = useMemo(() => <ChargesSidePanels job={job} />, [job]);

  useInvalidateQueriesOnInvoiceUpdate(companyId, job.id, [
    { queryKey: [makeJobsBillingLinesQueryKey(order.id)] },
    makeBillingLineGroupsWithJobsFilters([job.id]),
    { queryKey: jobInvoicesQueryKey(companyId, job.id) },
  ]);

  const handleJobUpdated = useCallback(
    (updatedJob: Job) => {
      updateJobInQueryData(
        JobInvoicing.fromJobOrJobInvoicingAndInvoices(
          updatedJob,
          job.receivableInvoice,
          job.payableInvoice,
        ),
      );
      reload();
    },
    [job.payableInvoice, job.receivableInvoice, reload, updateJobInQueryData],
  );

  return (
    <Box sx={(theme) => ({ bgcolor: theme.brandV2.colors.treadGray8, p: 2 })}>
      <ApprovalCardElevationContext.Provider value>
        <ApproveJobsJobDetails
          belowApprovals={filesCard}
          belowTimesheets={sidePanels}
          columnWidths={REVIEW_COLUMN_WIDTHS}
          includeTimelineInJobDetailsTab
          job={job}
          onJobUpdated={handleJobUpdated}
        />
      </ApprovalCardElevationContext.Provider>
    </Box>
  );
}

const nestedTableCardSx = (theme: Theme) => ({
  ...chargesRaisedCardSx(theme),
  mx: 2,
  overflow: 'hidden',
});

const nestedSectionTitleSx = {
  fontSize: '14px',
  fontWeight: 700,
  lineHeight: '20px',
  pb: 1,
  pt: 2,
  px: 2,
} as const;

interface ChargesJobFrameProps {
  children: ReactNode;
  isOpen: boolean;
}

function ChargesJobFrame({ children, isOpen }: ChargesJobFrameProps) {
  return (
    <Box data-test-id="charges-job-section">
      <Box
        sx={(theme) => ({
          position: 'relative',
          ...(isOpen && {
            '&::before': {
              bgcolor: theme.brandV2.colors.treadBlue,
              bottom: 0,
              content: '""',
              left: 0,
              position: 'absolute',
              top: 0,
              width: OPEN_JOB_RAIL_WIDTH,
              zIndex: 2,
            },
          }),
        })}
      >
        {children}
      </Box>
      {isOpen && (
        <Box
          data-test-id="charges-job-gap"
          sx={(theme) => ({
            bgcolor: theme.semantic.surfacePage,
            height: theme.spacing(1),
          })}
        />
      )}
    </Box>
  );
}

interface TimeBasedSideNotesProps {
  charges: JobCharges;
  sides: BillingLineSide[];
}

function TimeBasedSideNotes({ charges, sides }: TimeBasedSideNotesProps) {
  const { currencyFormatter } = useCompanyCurrency();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, px: 2, py: 1 }}>
      {sides.map((side) => {
        const settlementNumbers = uniq(
          getLinesOfSide(charges.lines, side)
            .filter((line) => !line.isRejected)
            .map(getLineSettlementNumber)
            .filter(isString),
        );

        return (
          <Box
            key={side}
            data-test-id={`charges-time-based-side-${side}`}
            sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}
          >
            <InfoOutlined
              sx={(theme) => ({ color: theme.brandV2.colors.treadBlue, fontSize: 16 })}
            />
            <Typography
              sx={(theme) => ({
                color: theme.brandV2.colors.treadGray2,
                fontSize: '12px',
              })}
            >
              {t(timeBasedSideNoteKeys[side], {
                settlements:
                  settlementNumbers.join(', ') ||
                  t('approvals.approve_charges.unsettled'),
                total: currencyFormatter(charges[side].total),
              })}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

interface JobAddOnsProps {
  charges?: JobCharges;
  excludedSides: BillingLineSide[];
  job: JobInvoicing;
}

function JobAddOns({ charges, excludedSides, job }: JobAddOnsProps) {
  const sideTotal = (side: BillingLineSide) =>
    excludedSides.includes(side) ? 0 : (charges?.[side].addOnsTotal ?? 0);

  return (
    <Box>
      <ChargeRowsTable
        nameHeading={t('approvals.approve_charges.columns.add_ons')}
        rows={(charges?.addOns ?? []).filter((row) => !excludedSides.includes(row.side))}
        totalsActions={
          <ChargesAddOnActions
            charges={charges}
            job={job}
            sides={BILLING_LINE_SIDES.filter((side) => !excludedSides.includes(side))}
          />
        }
        totals={{
          label: t('approvals.approve_charges.add_on_totals'),
          payable: sideTotal(BillingLineSide.PAYABLE),
          receivable: sideTotal(BillingLineSide.RECEIVABLE),
        }}
      />
    </Box>
  );
}
